import React, { useState, useEffect, useContext, useRef } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
} from '@mui/material';
import {
	Add as AddIcon,
	Send as SendIcon,
	Visibility as VisibilityIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	Refresh as RefreshIcon,
	ContentCopy as ContentCopyIcon,
	Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import OlympicsContext from '../../store/olympics-context';
import EmailCampaign from '../../model/EmailCampaign';
import EmailCampaignRecipient from '../../model/EmailCampaignRecipient';
import {
	getAllCampaigns,
	getMyVisibleCampaigns,
	sendCampaign,
	deleteCampaign,
	getCampaignDetails,
} from '../../api/email-campaign-api';
import NewEmailCampaignDialog from './NewEmailCampaignDialog';
import EmailCampaignRecipientsTable from './EmailCampaignRecipientsTable';
// PanelHeader is rendered globally in PanelPage

const EmailCampaignsPage: React.FC = () => {
	const { token, role } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const { districtFilterId, searchFilterValue, refreshTablesTrigger, setIsRefreshing, districts } =
		useContext(OlympicsContext);
	const navigate = useNavigate();

	const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
	const [displayedCampaigns, setDisplayedCampaigns] = useState<EmailCampaign[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const isRefreshingRef = useRef(false);
	const [isSending, setIsSending] = useState<number | null>(null); // campaignId being sent
	const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
	const [campaignDialogMode, setCampaignDialogMode] = useState<'create' | 'edit' | 'duplicate'>('create');
	const [campaignDialogInitialData, setCampaignDialogInitialData] = useState<any | null>(null);
	const [campaignDialogPdfName, setCampaignDialogPdfName] = useState<string | null>(null);
	const [campaignDialogId, setCampaignDialogId] = useState<number | null>(null);
	const [campaignDialogFocusDistricts, setCampaignDialogFocusDistricts] = useState(false);
	const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
	const [rateLimitInfo, setRateLimitInfo] = useState<{ until: string; campaignName: string } | null>(null);
	const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
	const [detailsLoading, setDetailsLoading] = useState(false);
	const [detailsError, setDetailsError] = useState('');
	const [detailsCampaign, setDetailsCampaign] = useState<any | null>(null);

	const isAdmin = role === 'administrator';

	// Load data on mount or when refresh trigger changes
	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			// First load
			downloadData(false);
		} else {
			// Refresh triggered
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	useEffect(() => {
		filterCampaigns();
	}, [campaigns, statusFilter, visibilityFilter, searchFilterValue]);

	const downloadData = async (isRefresh = false) => {
		if (!token) return;

		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}

		try {
			let response;

			if (isAdmin) {
				response = await getAllCampaigns(token);
			} else {
				response = await getMyVisibleCampaigns(token);
			}

			if (response.status === 200) {
				const campaignsData = response.data.map((d: any) => EmailCampaign.fromApiResponse(d));
				setCampaigns(campaignsData);
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas pobierania wysyłek' });
			}
		} catch (exception: any) {
			console.error(exception);
			if (exception.response?.status === 401 || exception.response?.status === 403) {
				setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
			} else {
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
			}
		} finally {
			setIsLoading(false);
			if (isRefresh && isRefreshingRef.current) {
				isRefreshingRef.current = false;
				setIsRefreshing(false);
			}
		}
	};

	const filterCampaigns = () => {
		let filtered = [...campaigns];

		// Status filter
		if (statusFilter !== 'all') {
			filtered = filtered.filter((campaign) => {
				switch (statusFilter) {
					case 'active':
						return campaign.isActive;
					case 'inactive':
						return !campaign.isActive;
					case 'test':
						return campaign.isTest;
					case 'sent':
						return campaign.sentAt;
					case 'draft':
						return !campaign.sentAt;
					default:
						return true;
				}
			});
		}

		// Visibility filter (admin only)
		if (isAdmin && visibilityFilter !== 'all') {
			filtered = filtered.filter((campaign) => campaign.visibleTo === visibilityFilter);
		}

		// Search filter
		if (searchFilterValue) {
			filtered = filtered.filter((campaign) => {
				const searchLower = searchFilterValue.toLowerCase();
				return (
					campaign.name.toLowerCase().includes(searchLower) ||
					campaign.subject.toLowerCase().includes(searchLower) ||
					campaign.message.toLowerCase().includes(searchLower)
				);
			});
		}

		setDisplayedCampaigns(filtered);
	};

	const handleSendCampaign = async (campaignId: number) => {
		if (!token || isSending) return;

		// Prośba o pozwolenie na powiadomienia przeglądarki (tylko gdy użytkownik klika "Wyślij")
		if ('Notification' in window && Notification.permission === 'default') {
			await Notification.requestPermission();
		}

		try {
			setIsSending(campaignId); // Start loading state
			const response = await sendCampaign(token, campaignId);
			if (response.status === 200 && response.data) {
				// Sprawdź czy wykryto rate limit
				if (response.data.rate_limit_detected && response.data.rate_limit_until) {
					const campaign = campaigns.find((c) => c.id === campaignId);
					setRateLimitInfo({
						until: response.data.rate_limit_until,
						campaignName: campaign?.name || 'Kampania',
					});
					setMsg({
						msg: response.data.message || 'Wykryto rate limit Gmail! Wysyłka została wstrzymana.',
						severity: 'error',
					});

					// Powiadomienie przeglądarki (jeśli wspierane i dozwolone)
					if ('Notification' in window && Notification.permission === 'granted') {
						new Notification('⚠️ Rate limit Gmail', {
							body: `Wysyłka kampanii "${campaign?.name}" została wstrzymana z powodu rate limitu Gmail. Blokada do ${response.data.rate_limit_until}`,
							icon: '/favicon.ico',
							tag: 'rate-limit-' + campaignId,
						});
					}
				} else {
					// Normalna wysyłka
					const message =
						response.data.message || `Wysłano ${response.data.sent} wiadomości. Pozostało ${response.data.remaining}.`;
					setMsg({ msg: message, severity: 'success' });
					setRateLimitInfo(null); // Wyczyść rate limit info jeśli wysyłka się powiodła
				}
				downloadData(true); // Silent refresh
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas wysyłania', severity: 'error' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas wysyłania wiadomości', severity: 'error' });
		} finally {
			setIsSending(null); // End loading state
		}
	};

	const handleDeleteCampaign = async (campaignId: number) => {
		if (!token) return;

		if (!window.confirm('Czy na pewno chcesz usunąć tę wysyłkę?')) {
			return;
		}

		try {
			const response = await deleteCampaign(token, campaignId);
			if (response.status === 200) {
				setMsg({ msg: 'Wysyłka została usunięta' });
				downloadData(true); // Silent refresh
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas usuwania' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas usuwania wysyłki' });
		}
	};

	const handleViewRecipients = (campaign: EmailCampaign) => {
		setSelectedCampaign(campaign);
	};

	const handleCloseRecipients = () => {
		setSelectedCampaign(null);
	};

	const handleRefresh = () => {
		downloadData();
	};

	const getStatusChip = (campaign: EmailCampaign) => {
		if (campaign.isTest) {
			return (
				<Chip
					label='Test'
					color='warning'
					size='small'
				/>
			);
		}
		if (!campaign.isActive) {
			return (
				<Chip
					label='Nieaktywna'
					color='default'
					size='small'
				/>
			);
		}
		if (campaign.sentAt) {
			return (
				<Chip
					label='Wysłana'
					color='success'
					size='small'
				/>
			);
		}
		return (
			<Chip
				label='Szkic'
				color='info'
				size='small'
			/>
		);
	};

	const getVisibilityText = (visibleTo: string) => {
		switch (visibleTo) {
			case 'admin_only':
				return 'Tylko administrator';
			case 'admin_secretary':
				return 'Admin + Sekretarze';
			case 'admin_secretary_teacher':
				return 'Admin + Sekretarze + Nauczyciele';
			case 'all':
				return 'Wszyscy';
			default:
				return visibleTo;
		}
	};

	const getTargetPresetLabel = (preset?: string) => {
		switch (preset) {
			case 'all_teachers':
				return 'Wszyscy nauczyciele';
			case 'all_secretaries':
				return 'Wszyscy sekretarze';
			case 'all_secretaries_and_teachers':
				return 'Wszyscy sekretarze i nauczyciele';
			case 'all_students':
				return 'Wszyscy uczniowie';
			case 'district_teachers':
				return 'Nauczyciele z wybranych okręgów';
			case 'district_secretaries':
				return 'Sekretarze z wybranych okręgów';
			case 'district_students':
				return 'Uczniowie z wybranych okręgów';
			case 'custom_sql':
				return 'Zaawansowane (SQL WHERE)';
			case 'stage3_signed_scan_cohort':
				return 'Zaświadczenia III — skany podpisane (kohorta)';
			default:
				return preset || '-';
		}
	};

	const getStudentFilterLabel = (filter?: string) => {
		switch (filter) {
			case 'none':
				return 'Brak';
			case 'approved':
				return 'Potwierdzony';
			case 'first_stage_points_approved':
				return 'Uzyskał punkty w etapie I (potwierdzony)';
			case 'first_stage_points_unapproved':
				return 'Uzyskał punkty w etapie I (niepotwierdzony)';
			case 'first_stage_qualified':
				return 'Zakwalifikowany do etapu IIA';
			case 'second_a_points':
				return 'Uzyskał punkty w etapie IIA';
			case 'second_a_qualified':
				return 'Zakwalifikowany do etapu IIB';
			case 'second_b_points':
				return 'Uzyskał punkty w etapie IIB';
			case 'second_b_oral_description':
				return 'Wpisana charakterystyka w etapie IIB';
			case 'second_b_qualified':
				return 'Zakwalifikowany do etapu IIIA';
			case 'third_a_points':
				return 'Uzyskał punkty w etapie IIIA';
			case 'third_a_qualified':
				return 'Zakwalifikowany do etapu IIIB';
			case 'third_b_points':
				return 'Uzyskał punkty w etapie IIIB';
			default:
				return filter || '-';
		}
	};

	const mapCampaignToFormData = (campaign: any) => ({
		name: campaign.name ?? '',
		subject: campaign.subject ?? '',
		message: campaign.message ?? '',
		target_preset: campaign.target_preset ?? 'all_teachers',
		target_districts: campaign.target_districts ?? [],
		target_custom_sql: campaign.target_custom_sql ?? '',
		stage3_scan_cohort: campaign.stage3_scan_cohort ?? '',
		require_link_verification: !!campaign.require_link_verification,
		require_password_verification: !!campaign.require_password_verification,
		require_code_verification: !!campaign.require_code_verification,
		enable_student_merge: !!campaign.enable_student_merge,
		student_filter: campaign.student_filter ?? 'none',
		is_test: !!campaign.is_test,
		is_active: !!campaign.is_active,
		visible_to: campaign.visible_to ?? 'admin_only',
	});

	const loadCampaignDetails = async (campaignId: number) => {
		if (!token) return null;
		try {
			const response = await getCampaignDetails(token, campaignId);
			if (response.status === 200 && response.data?.campaign) {
				return response.data.campaign;
			}
			setMsg({ msg: response.data?.message || 'Błąd podczas pobierania szczegółów wysyłki', severity: 'error' });
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas pobierania szczegółów wysyłki', severity: 'error' });
		}
		return null;
	};

	const handleOpenCreate = () => {
		setCampaignDialogMode('create');
		setCampaignDialogInitialData(null);
		setCampaignDialogPdfName(null);
		setCampaignDialogId(null);
		setCampaignDialogFocusDistricts(false);
		setCampaignDialogOpen(true);
	};

	const handleOpenEdit = async (campaignId: number) => {
		const campaign = await loadCampaignDetails(campaignId);
		if (!campaign) return;
		setCampaignDialogMode('edit');
		setCampaignDialogInitialData(mapCampaignToFormData(campaign));
		setCampaignDialogPdfName(campaign.pdf_attachment_path ?? null);
		setCampaignDialogId(campaignId);
		setCampaignDialogFocusDistricts(false);
		setCampaignDialogOpen(true);
	};

	const handleOpenDuplicate = async (campaignId: number) => {
		const campaign = await loadCampaignDetails(campaignId);
		if (!campaign) return;
		const formData = mapCampaignToFormData(campaign);
		formData.name = `${formData.name} (kopia)`;
		setCampaignDialogMode('duplicate');
		setCampaignDialogInitialData(formData);
		setCampaignDialogPdfName(campaign.pdf_attachment_path ?? null);
		setCampaignDialogId(null);
		setCampaignDialogFocusDistricts(false);
		setCampaignDialogOpen(true);
	};

	const handleOpenDuplicateWithDistrictFocus = async (campaignId: number) => {
		const campaign = await loadCampaignDetails(campaignId);
		if (!campaign) return;
		const formData = mapCampaignToFormData(campaign);
		formData.name = `${formData.name} (kopia)`;
		setCampaignDialogMode('duplicate');
		setCampaignDialogInitialData(formData);
		setCampaignDialogPdfName(campaign.pdf_attachment_path ?? null);
		setCampaignDialogId(null);
		setCampaignDialogFocusDistricts(true);
		setCampaignDialogOpen(true);
	};

	const handleOpenDetails = async (campaignId: number) => {
		setDetailsDialogOpen(true);
		setDetailsLoading(true);
		setDetailsError('');
		setDetailsCampaign(null);
		const campaign = await loadCampaignDetails(campaignId);
		if (campaign) {
			setDetailsCampaign(campaign);
		} else {
			setDetailsError('Nie udało się pobrać szczegółów wysyłki');
		}
		setDetailsLoading(false);
	};

	const copyToClipboard = async (text: string, successMessage: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setMsg({ msg: successMessage, severity: 'success' });
		} catch (error) {
			setMsg({ msg: 'Nie udało się skopiować do schowka', severity: 'error' });
		}
	};

	if (isLoading) {
		return (
			<Box
				display='flex'
				justifyContent='center'
				alignItems='center'
				minHeight='400px'
			>
				<CircularProgress />
			</Box>
		);
	}

	if (selectedCampaign) {
		return (
			<Box>
				<Box sx={{ mb: 3 }}>
					<Typography
						variant='h5'
						sx={{ color: 'primary.main', mb: 2 }}
					>
						Odbiorcy wysyłki: {selectedCampaign.name}
					</Typography>
				</Box>
				<EmailCampaignRecipientsTable
					campaignId={selectedCampaign.id}
					campaignName={selectedCampaign.name}
					onClose={handleCloseRecipients}
				/>
			</Box>
		);
	}

	return (
		<Box>
			{rateLimitInfo && (
				<Alert
					severity='error'
					sx={{ mb: 3 }}
					onClose={() => setRateLimitInfo(null)}
				>
					<strong>⚠️ Rate limit Gmail wykryty!</strong>
					<br />
					Wysyłka kampanii "{rateLimitInfo.campaignName}" została wstrzymana z powodu przekroczenia limitu wysyłki
					Gmail.
					<br />
					<strong>Blokada do: {rateLimitInfo.until}</strong>
					<br />
					Powiadomienie zostało wysłane do administratora. Możesz wznowić wysyłkę po upływie blokady.
				</Alert>
			)}
			<Box sx={{ mb: 3 }}>
				<Typography
					variant='h5'
					sx={{ color: 'primary.main', mb: 2 }}
				>
					Wysyłki mailingowe
				</Typography>
			</Box>

			{/* Filters */}
			<Box
				display='flex'
				gap={2}
				mb={3}
				alignItems='center'
			>
				<FormControl
					size='small'
					sx={{ minWidth: 150 }}
				>
					<InputLabel>Status</InputLabel>
					<Select
						value={statusFilter}
						label='Status'
						onChange={(e) => setStatusFilter(e.target.value)}
					>
						<MenuItem value='all'>Wszystkie</MenuItem>
						<MenuItem value='active'>Aktywne</MenuItem>
						<MenuItem value='inactive'>Nieaktywne</MenuItem>
						<MenuItem value='test'>Testowe</MenuItem>
						<MenuItem value='sent'>Wysłane</MenuItem>
						<MenuItem value='draft'>Szkice</MenuItem>
					</Select>
				</FormControl>

				{isAdmin && (
					<FormControl
						size='small'
						sx={{ minWidth: 200 }}
					>
						<InputLabel>Widoczność</InputLabel>
						<Select
							value={visibilityFilter}
							label='Widoczność'
							onChange={(e) => setVisibilityFilter(e.target.value)}
						>
							<MenuItem value='all'>Wszystkie</MenuItem>
							<MenuItem value='admin_only'>Tylko administrator</MenuItem>
							<MenuItem value='admin_secretary'>Admin + Sekretarze</MenuItem>
							<MenuItem value='admin_secretary_teacher'>Admin + Sekretarze + Nauczyciele</MenuItem>
							<MenuItem value='all'>Wszyscy</MenuItem>
						</Select>
					</FormControl>
				)}

				{isAdmin && (
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={handleOpenCreate}
						sx={{ ml: 'auto' }}
					>
						Nowa wysyłka
					</Button>
				)}
			</Box>

			{/* Campaigns Table */}
			<TableContainer
				component={Paper}
				sx={{
					maxHeight: 'calc(100vh - 310px)',
					overflowX: 'auto',
					maxWidth: 'calc(100% - 30px)',
				}}
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Nazwa</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Widoczność</TableCell>
							<TableCell>Data wysłania</TableCell>
							<TableCell>Statystyki</TableCell>
							<TableCell align='center'>Akcje</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{displayedCampaigns.map((campaign) => (
							<TableRow
								key={campaign.id}
								hover
							>
								<TableCell>
									<Typography
										variant='body2'
										fontWeight='medium'
									>
										{campaign.name}
									</Typography>
								</TableCell>
								<TableCell>{getStatusChip(campaign)}</TableCell>
								<TableCell>
									<Typography
										variant='body2'
										fontSize='0.875rem'
									>
										{getVisibilityText(campaign.visibleTo)}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant='body2'>
										{campaign.sentAt ? new Date(campaign.sentAt).toLocaleString('pl-PL') : '-'}
									</Typography>
								</TableCell>
								<TableCell>
									{campaign.statistics && (
										<Typography
											variant='body2'
											fontSize='0.875rem'
										>
											{campaign.statistics.sent}/{campaign.statistics.total} wysłano
										</Typography>
									)}
								</TableCell>
								<TableCell align='center'>
									<Box
										display='flex'
										gap={1}
										justifyContent='center'
									>
										<Tooltip title='Zobacz odbiorców'>
											<IconButton
												size='small'
												onClick={() => handleViewRecipients(campaign)}
											>
												<VisibilityIcon />
											</IconButton>
										</Tooltip>
										<Tooltip title='Szczegóły wysyłki'>
											<IconButton
												size='small'
												onClick={() => handleOpenDetails(campaign.id)}
											>
												<InfoIcon />
											</IconButton>
										</Tooltip>

										{isAdmin && !campaign.sentAt && (
											<Tooltip title='Edytuj'>
												<IconButton
													size='small'
													onClick={() => handleOpenEdit(campaign.id)}
												>
													<EditIcon />
												</IconButton>
											</Tooltip>
										)}

										{isAdmin && (
											<Tooltip title='Utwórz na podstawie'>
												<IconButton
													size='small'
													onClick={() => handleOpenDuplicate(campaign.id)}
												>
													<ContentCopyIcon />
												</IconButton>
											</Tooltip>
										)}

										{isAdmin &&
											campaign.statistics &&
											campaign.statistics.total > 0 &&
											campaign.statistics.sent < campaign.statistics.total && (
												<Tooltip
													title={
														isSending === campaign.id ? 'Wysyłanie...' : campaign.sentAt ? 'Wznów wysyłkę' : 'Wyślij'
													}
												>
													<IconButton
														size='small'
														onClick={() => handleSendCampaign(campaign.id)}
														disabled={isSending !== null}
														color={campaign.sentAt ? 'warning' : 'primary'}
													>
														{isSending === campaign.id ? <CircularProgress size={20} /> : <SendIcon />}
													</IconButton>
												</Tooltip>
											)}

										{isAdmin && (
											<Tooltip title='Usuń'>
												<IconButton
													size='small'
													onClick={() => handleDeleteCampaign(campaign.id)}
													color='error'
												>
													<DeleteIcon />
												</IconButton>
											</Tooltip>
										)}
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{displayedCampaigns.length === 0 && (
				<Alert
					severity='info'
					sx={{ mt: 2 }}
				>
					Brak wysyłek spełniających kryteria wyszukiwania.
				</Alert>
			)}

			{/* New Campaign Dialog */}
			<NewEmailCampaignDialog
				open={campaignDialogOpen}
				onClose={() => setCampaignDialogOpen(false)}
				onSuccess={() => {
					setCampaignDialogOpen(false);
					downloadData();
				}}
				mode={campaignDialogMode}
				campaignId={campaignDialogId ?? undefined}
				initialData={campaignDialogInitialData ?? undefined}
				initialPdfAttachmentName={campaignDialogPdfName}
				focusDistricts={campaignDialogFocusDistricts}
			/>

			{/* Details Dialog */}
			<Dialog
				open={detailsDialogOpen}
				onClose={() => setDetailsDialogOpen(false)}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle>Szczegóły wysyłki</DialogTitle>
				<DialogContent>
					{detailsLoading && (
						<Box
							display='flex'
							justifyContent='center'
							alignItems='center'
							minHeight='200px'
						>
							<CircularProgress />
						</Box>
					)}
					{detailsError && <Alert severity='error'>{detailsError}</Alert>}
					{!detailsLoading && detailsCampaign && (
						<Box
							display='flex'
							flexDirection='column'
							gap={2}
							pt={1}
						>
							<TextField
								label='Nazwa wysyłki'
								value={detailsCampaign.name || ''}
								fullWidth
								InputProps={{ readOnly: true }}
							/>
							<Box
								display='flex'
								gap={1}
								alignItems='center'
							>
								<TextField
									label='Temat wiadomości'
									value={detailsCampaign.subject || ''}
									fullWidth
									InputProps={{ readOnly: true }}
								/>
								<Tooltip title='Kopiuj temat'>
									<IconButton onClick={() => copyToClipboard(detailsCampaign.subject || '', 'Temat skopiowany')}>
										<ContentCopyIcon />
									</IconButton>
								</Tooltip>
							</Box>
							<Box
								display='flex'
								gap={1}
								alignItems='flex-start'
							>
								<TextField
									label='Treść wiadomości'
									value={detailsCampaign.message || ''}
									fullWidth
									multiline
									rows={6}
									InputProps={{ readOnly: true }}
								/>
								<Tooltip title='Kopiuj treść'>
									<IconButton
										onClick={() => copyToClipboard(detailsCampaign.message || '', 'Treść skopiowana')}
										sx={{ mt: 1 }}
									>
										<ContentCopyIcon />
									</IconButton>
								</Tooltip>
							</Box>
							<Box>
								<Typography variant='subtitle2'>Ustawienia</Typography>
								<Typography variant='body2'>
									<strong>Grupa docelowa:</strong> {getTargetPresetLabel(detailsCampaign.target_preset)}
								</Typography>
								{detailsCampaign.target_preset === 'stage3_signed_scan_cohort' &&
									(detailsCampaign.stage3_scan_cohort || detailsCampaign.stage3ScanCohort) && (
										<Typography variant='body2'>
											<strong>Kohorta zaświadczeń III:</strong>{' '}
											{detailsCampaign.stage3_scan_cohort || detailsCampaign.stage3ScanCohort}
										</Typography>
									)}
								{Array.isArray(detailsCampaign.target_districts) && detailsCampaign.target_districts.length > 0 && (
									<Typography variant='body2'>
										<strong>Okręgi:</strong>{' '}
										{detailsCampaign.target_districts
											.map((id: number) => districts.find((d) => d.id === id)?.name || id)
											.join(', ')}
									</Typography>
								)}
								{detailsCampaign.target_custom_sql && (
									<Typography variant='body2'>
										<strong>SQL:</strong> {detailsCampaign.target_custom_sql}
									</Typography>
								)}
								<Typography variant='body2'>
									<strong>Weryfikacja linkiem:</strong> {detailsCampaign.require_link_verification ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Weryfikacja hasłem:</strong> {detailsCampaign.require_password_verification ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Weryfikacja kodem:</strong> {detailsCampaign.require_code_verification ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Scalanie danych uczniów:</strong> {detailsCampaign.enable_student_merge ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Filtr uczniów:</strong> {getStudentFilterLabel(detailsCampaign.student_filter || 'none')}
								</Typography>
								<Typography variant='body2'>
									<strong>Załącznik PDF:</strong> {detailsCampaign.pdf_attachment_path || 'Brak'}
								</Typography>
								<Typography variant='body2'>
									<strong>Wysyłka testowa:</strong> {detailsCampaign.is_test ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Aktywna:</strong> {detailsCampaign.is_active ? 'Tak' : 'Nie'}
								</Typography>
								<Typography variant='body2'>
									<strong>Widoczność:</strong> {getVisibilityText(detailsCampaign.visible_to || 'admin_only')}
								</Typography>
								<Typography variant='body2'>
									<strong>Data wysłania:</strong>{' '}
									{detailsCampaign.sent_at ? new Date(detailsCampaign.sent_at).toLocaleString('pl-PL') : '-'}
								</Typography>
							</Box>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{isAdmin && detailsCampaign && (
						<>
							<Button
								onClick={() => {
									setDetailsDialogOpen(false);
									handleOpenDuplicate(detailsCampaign.id);
								}}
								startIcon={<ContentCopyIcon />}
							>
								Utwórz na podstawie
							</Button>
							{(detailsCampaign.target_preset || detailsCampaign.targetPreset)?.startsWith('district_') && (
								<Button
									onClick={() => {
										setDetailsDialogOpen(false);
										handleOpenDuplicateWithDistrictFocus(detailsCampaign.id);
									}}
									startIcon={<ContentCopyIcon />}
									variant='outlined'
								>
									Duplikuj i edytuj okręgi
								</Button>
							)}
						</>
					)}
					<Button onClick={() => setDetailsDialogOpen(false)}>Zamknij</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default EmailCampaignsPage;
